/**
 * „Richtlinien-Werkstatt" — so arbeitet ein Arbeitskreis an einer technischen Regel (GEFMA 190),
 * wenn sie als Wissensgraph statt als Word-Datei vorliegt (Prototyp, 26.10.2026).
 *
 * Links die Gliederung, in der Mitte der Abschnitt mit erkannten Anforderungen und Belegstellen,
 * rechts die Kanten ins Recht zum Bestätigen oder Verwerfen, die Anforderungen des Abschnitts und die
 * Prüfliste nach Rechtsänderungen seit der Ausgabe. Entwürfe ändern die geltende Fassung nicht.
 */

import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useOutletContext, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAtom } from "jotai";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { toast } from "sonner";
import { userAPI } from "../UserAPI";
import { configuration } from "../Configuration";
import { jwtAuthService } from "../JWTAuthService";
import { colorFor } from "./plate/Collaboration";
import type { SectionContent } from "@the-real-insight/tri-model";
import { StsDialog } from "./StsDialog";
import { ProvisionDialog } from "./ProvisionDialog";
import { ExportMenu } from "./ExportMenu";
import { displayTitle } from "./guidelinesPath";
import { ImportDialog } from "./ImportDialog";
import { NewGuidelineDialog } from "./NewGuidelineDialog";
import { GlossaryPanel } from "./GlossaryPanel";
import { DashboardPanel } from "./DashboardPanel";
import { TermsPanel } from "./TermsPanel";
import { GlobalSearch } from "./GlobalSearch";
import { InboxPanel } from "./InboxPanel";
import { VersionsDialog } from "./VersionsDialog";
import { RequirementsPanel } from "./RequirementsPanel";
import { AlertTriangle, ArrowLeft, BookCheck, BookOpen, Check, Code2, ExternalLink, FilePlus2, FileText, FileUp, History, MessageSquarePlus, Pencil, RotateCcw, Save, Sparkles, X } from "lucide-react";

import type { AppShellContext } from "../components/AppShell";
import ContentPanel from "../components/ContentPanel";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from "../components/ui/resizable";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { cn } from "../lib/utils";
import { authenticationAtom } from "../State";
import {
  useGuidelinesAPI,
  type GuidelineLink,
  type GuidelinePermissions,
  type Discussion,
  type GuidelineSection,
  type GuidelineSummary,
  type GuidelineWorkspace,
  type LinkStatus,
  type RequirementKind,
  type WorkingGroupRef,
} from "./GuidelinesAPI";
import { GUIDELINES_PATH, displayNumber } from "./guidelinesPath";
import { SectionTree } from "./SectionTree";
import { EditionDialog } from "./EditionDialog";
import { GuidelinePdfView, type PdfMark } from "./GuidelinePdfView";
import "./guidelines.css";
import type { PlateEditorHandle, PlateMarkdownEditorLabels } from "./plate/PlateMarkdownEditor.types";
import { collectSuggestions, type SuggestionSummary } from "./plate/plateValue";
import { clearMarks, linkAtPoint, paintMarks, paintSearchTerms, type TextMark } from "./textMarks";
import { parseQuery, searchSections } from "./search";
import { collectCommentIds } from "./plate/plateValue";
import { SearchResults } from "./SearchResults";
import { CommentsPanel, SuggestionsPanel } from "./ReviewPanels";

// Plate nachladen — es bleibt aus dem Hauptbündel.
const PlateMarkdownEditor = lazy(() => import("./plate/PlateMarkdownEditor"));
const PlateMarkdownView = lazy(() => import("./plate/PlateMarkdownView"));

const REQUIREMENT_CLASS: Record<RequirementKind, string> = {
  muss: "bg-amber-100 dark:bg-amber-900/40",
  soll: "bg-sky-100 dark:bg-sky-900/40",
  kann: "bg-slate-100 dark:bg-slate-800/60",
};

const STATUS_VARIANT: Record<LinkStatus, "success" | "warning" | "outline"> = {
  confirmed: "success",
  proposed: "warning",
  rejected: "outline",
};

export { displayTitle } from "./guidelinesPath";

/** Die stärkste Stufe für die Anzeige: Leitung prüft, Autor bearbeitet, sonst lesen. */
const accessLevel = (p: GuidelinePermissions) => (p.reviewLinks ? "lead" : p.editText ? "author" : p.approve ? "board" : "reader");

const errorText = (error: unknown) => (error instanceof Error ? error.message : String(error));

export default function GuidelinesPage() {
  const { t } = useTranslation();
  const { setTitle } = useOutletContext<AppShellContext>();
  const { documentId } = useParams();
  const [authentication] = useAtom(authenticationAtom);
  const customer = authentication?.customer ?? "";

  useEffect(() => {
    setTitle(t("guidelinesPage.title"));
  }, [setTitle, t]);

  return (
    <ContentPanel backgroundColor="transparent" paddingClassName="p-3 sm:p-4">
      {!customer ? null : documentId ? (
        <Workspace customer={customer} documentId={documentId} />
      ) : (
        <GuidelineOverview customer={customer} />
      )}
    </ContentPanel>
  );
}

/** Übersicht der Werkstatt: Richtlinien, Glossar des Mandanten und — für Vorstand und Geschäftsstelle — die Lage des Bestands. */
function GuidelineOverview({ customer }: { customer: string }) {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [flags, setFlags] = useState({ glossary: false, dashboard: false });
  const wanted = searchParams.get("view") ?? "list";
  const view = (wanted === "glossary" && flags.glossary) || (wanted === "dashboard" && flags.dashboard) ? wanted : "list";
  const choose = (v: string) => {
    const next = new URLSearchParams(searchParams);
    if (v === "list") next.delete("view");
    else next.set("view", v);
    setSearchParams(next, { replace: true });
  };
  const list = <GuidelineList customer={customer} onIndex={(r) => setFlags({ glossary: r.items.some((i) => i.permissions?.comment), dashboard: r.canSeeDashboard })} />;
  if (!flags.glossary && !flags.dashboard) return list;
  return (
    <Tabs value={view} onValueChange={choose} className="flex flex-col gap-3">
      <TabsList className="self-start">
        <TabsTrigger value="list">{t("guidelinesPage.overview.list")}</TabsTrigger>
        {flags.glossary ? <TabsTrigger value="glossary">{t("guidelinesPage.overview.glossary")}</TabsTrigger> : null}
        {flags.dashboard ? <TabsTrigger value="dashboard">{t("guidelinesPage.overview.dashboard")}</TabsTrigger> : null}
      </TabsList>
      <TabsContent value="list" className="mt-0">{list}</TabsContent>
      {flags.glossary ? <TabsContent value="glossary" className="mt-0 max-w-4xl"><GlossaryPanel customer={customer} /></TabsContent> : null}
      {flags.dashboard ? <TabsContent value="dashboard" className="mt-0 max-w-7xl"><DashboardPanel customer={customer} /></TabsContent> : null}
    </Tabs>
  );
}

function GuidelineList({ customer, onIndex }: { customer: string; onIndex?: (r: { items: GuidelineSummary[]; canSeeDashboard: boolean }) => void }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const api = useGuidelinesAPI(customer);
  const [items, setItems] = useState<GuidelineSummary[] | null>(null);
  const [canImport, setCanImport] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [workingGroups, setWorkingGroups] = useState<WorkingGroupRef[]>([]);

  useEffect(() => {
    api
      .getGuidelineIndex()
      .then((r) => {
        setItems(r.items);
        setCanImport(r.canImport);
        setWorkingGroups(r.workingGroups);
        onIndex?.(r);
      })
      .catch((error) => toast.error(errorText(error)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer]);

  if (!items) return null;

  return (
    <div className="flex max-w-3xl flex-col gap-3">
      <div className="flex items-start gap-3">
        <p className="flex-1 text-sm text-muted-foreground">{items.length ? t("guidelinesPage.intro") : t("guidelinesPage.empty")}</p>
        {canImport ? (
          <div className="flex shrink-0 flex-wrap gap-2">
            <Button size="sm" variant="outline" onClick={() => setCreateOpen(true)}>
              <FilePlus2 size={14} className="mr-1" />
              {t("guidelinesPage.newGuideline.button")}
            </Button>
            <Button size="sm" variant="outline" onClick={() => setImportOpen(true)}>
              <FileUp size={14} className="mr-1" />
              {t("guidelinesPage.import.button")}
            </Button>
          </div>
        ) : null}
      </div>
      <InboxPanel customer={customer} />
      <GlobalSearch customer={customer} />
      <ImportDialog customer={customer} open={importOpen} onOpenChange={setImportOpen} onCreated={(id) => navigate(`${GUIDELINES_PATH}/${id}`)} />
      <NewGuidelineDialog customer={customer} open={createOpen} onOpenChange={setCreateOpen} guidelines={items} workingGroups={workingGroups} onCreated={(id) => navigate(`${GUIDELINES_PATH}/${id}`)} />
      {items.map((item) => (
        <button
          key={item._id}
          type="button"
          onClick={() => navigate(`${GUIDELINES_PATH}/${item._id}`)}
          className="flex items-center gap-3 rounded-lg border bg-card p-4 text-left hover:bg-accent"
        >
          <FileText size={20} className="shrink-0 text-muted-foreground" />
          <div className="min-w-0 flex-1">
            <div className="font-semibold">{displayTitle(item.title)}</div>
            <div className="text-xs text-muted-foreground">
              {t("guidelinesPage.sectionCount", { count: item.sectionCount })}
              {item.date ? ` · ${t("guidelinesPage.editionLabel")} ${new Date(item.date).toLocaleDateString("de-DE", { month: "2-digit", year: "numeric" })}` : ""}
            </div>
          </div>
          {item.superseded ? <Badge variant="outline">{t("guidelinesPage.edition.superseded")}</Badge> : null}
          {item.workingGroup ? <Badge variant="outline">{item.workingGroup.name}</Badge> : null}
          <Badge variant={item.writable ? "default" : "outline"}>{t(`guidelinesPage.access.${accessLevel(item.permissions)}`)}</Badge>
        </button>
      ))}
    </div>
  );
}

function Workspace({ customer, documentId }: { customer: string; documentId: string }) {
  const { t } = useTranslation();
  const api = useGuidelinesAPI(customer);
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState<GuidelineWorkspace | null>(null);
  // Aus der Suche über alle Richtlinien kommt der Begriff mit (?q=).
  const [filter, setFilter] = useState(() => searchParams.get("q") ?? "");
  const [showRequirements, setShowRequirements] = useState(true);
  const [selectedLink, setSelectedLink] = useState<string | null>(null);
  // Aus „Offen für dich“: Reiter (?tab=) und Freigabedialog (?edition=open) gleich offen.
  const [tab, setTab] = useState(() => searchParams.get("tab") ?? "links");
  // Original-PDF oder Textansicht; Entwürfe und neue Abschnitte gibt es nur als Text.
  const [view, setView] = useState<"pdf" | "text">("pdf");
  const [editionOpen, setEditionOpen] = useState(() => searchParams.get("edition") === "open");
  const navigate = useNavigate();
  const [authentication] = useAtom(authenticationAtom);
  const currentUser = String(authentication?.email ?? "");
  const currentUserName = [authentication?.firstName, authentication?.lastName].filter(Boolean).join(" ");
  // Kommentare und Vorschläge: Fäden vom Server, Vorschläge live aus dem Editor (sonst aus dem Entwurf).
  const [discussions, setDiscussions] = useState<Discussion[]>([]);
  const [pendingComment, setPendingComment] = useState<{ discussionId: string; quote: string; element?: string | null } | null>(null);
  const [selectedDiscussion, setSelectedDiscussion] = useState<string | null>(null);
  const [liveSuggestions, setLiveSuggestions] = useState<SuggestionSummary[] | null>(null);
  const editorHandle = useRef<PlateEditorHandle | null>(null);
  const saveRef = useRef<(() => Promise<void>) | null>(null);
  /** Inhalt beim Öffnen des Editors — Vergleich für die Rückfrage bei ungespeicherten Änderungen. */
  const editorSnapshot = useRef<string | null>(null);

  const reload = useCallback(
    () => api.getWorkspace(documentId).then(setData).catch((error) => toast.error(errorText(error))),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [customer, documentId],
  );

  useEffect(() => {
    reload();
  }, [reload]);

  const loadDiscussions = useCallback(
    () => api.listDiscussions(documentId).then(setDiscussions).catch((error) => toast.error(errorText(error))),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [customer, documentId],
  );
  useEffect(() => {
    loadDiscussions();
  }, [loadDiscussions]);

  const sections = useMemo(() => data?.sections ?? [], [data]);
  const sectionNumber = searchParams.get("section") ?? sections[0]?.number;
  // Alte Links tragen „Anhang A“ — Anhänge heißen jetzt „A“.
  const section = sections.find((s) => s.number === sectionNumber || s.number === sectionNumber?.replace(/^Anhang\s+/, "")) ?? sections[0];

  const linksBySection = useMemo(() => {
    const map = new Map<string, GuidelineLink[]>();
    for (const link of data?.links ?? []) map.set(link.section, [...(map.get(link.section) ?? []), link]);
    return map;
  }, [data]);

  const stats = useMemo(() => {
    const links = data?.links ?? [];
    const reqs = sections.flatMap((s) => s.requirements);
    return {
      sections: sections.length,
      muss: reqs.filter((r) => r.kind === "muss").length,
      soll: reqs.filter((r) => r.kind === "soll").length,
      confirmed: links.filter((l) => l.status === "confirmed").length,
      proposed: links.filter((l) => l.status === "proposed").length,
      unlinked: sections.filter((s) => !(linksBySection.get(s._id) ?? []).some((l) => l.status !== "rejected")).length,
      edited: sections.filter((s) => s.draft?.text != null && !s.draft.isNew).length,
      added: sections.filter((s) => s.draft?.isNew).length,
      removed: sections.filter((s) => s.draft?.removed).length,
    };
  }, [data, sections, linksBySection]);

  useEffect(() => {
    const warn = (e: BeforeUnloadEvent) => {
      if (!unsavedChangesRef.current()) return;
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, []);
  /** Ungespeicherte Änderungen im Editor (nicht im Live-Betrieb — dort speichert der Server laufend)? */
  const unsavedChanges = () => {
    const h = editorHandle.current;
    return !!h && !section?.hasBlocks && editorSnapshot.current !== null && JSON.stringify(h.getValue()) !== editorSnapshot.current;
  };
  const unsavedChangesRef = useRef(unsavedChanges);
  unsavedChangesRef.current = unsavedChanges;
  const selectSection = (number: string) => {
    if (unsavedChanges() && !window.confirm(t("guidelinesPage.unsavedConfirm"))) return;
    setSelectedLink(null);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("section", number);
      return next;
    });
  };

  const updateLink = useCallback(
    async (link: GuidelineLink, status: LinkStatus) => {
      try {
        await api.setLinkStatus(documentId, link._id, status);
        setData((prev) => prev && { ...prev, links: prev.links.map((l) => (l._id === link._id ? { ...l, status } : l)) });
      } catch (error) {
        toast.error(errorText(error));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [customer, documentId],
  );

  const updateSection = (updated: GuidelineSection) =>
    setData((prev) => prev && { ...prev, sections: prev.sections.map((s) => (s._id === updated._id ? updated : s)) });

  // Gliederung und Kantenliste folgen der Auswahl, auch beim Einstieg über ?section=.
  useEffect(() => {
    if (section) document.querySelector(`[data-section-id="${section._id}"]`)?.scrollIntoView?.({ block: "nearest" });
  }, [section]);
  useEffect(() => {
    if (selectedLink) document.querySelector(`[data-link-id="${selectedLink}"]`)?.scrollIntoView?.({ block: "nearest", behavior: "smooth" });
  }, [selectedLink]);

  // Vorschläge des Abschnitts: live aus dem Editor, sonst aus dem gespeicherten Entwurf.
  const sectionSuggestions = liveSuggestions ?? (Array.isArray(section?.draft?.value) ? collectSuggestions(section!.draft!.value as any) : []);
  const sectionDiscussions = discussions.filter((d) => d.section === section?._id);

  const changeTimer = useRef<number | null>(null);
  const onEditorChange = () => {
    if (changeTimer.current) window.clearTimeout(changeTimer.current);
    changeTimer.current = window.setTimeout(() => {
      const h = editorHandle.current;
      if (!h) return;
      setLiveSuggestions(h.getSuggestions());
      // Eine Kommentarmarke ohne bekannten Faden kam von jemand anderem (gemeinsames Bearbeiten): Fäden nachladen.
      const known = new Set(discussionsRef.current.map((d) => d.discussionId));
      const unknown = collectCommentIds(h.getValue() as any).filter((id) => !known.has(id) && id !== pendingRef.current && !triedRef.current.has(id));
      if (unknown.length) {
        unknown.forEach((id) => triedRef.current.add(id));
        loadDiscussions();
      }
    }, 250);
  };
  // Marken offener Fäden im Editor sicherstellen (fehlen nach dem Wiederherstellen einer Fassung).
  const [handleTick, setHandleTick] = useState(0);
  useEffect(() => {
    const h = editorHandle.current;
    if (!h || !section) return;
    h.ensureCommentMarks(discussions.filter((d) => d.section === section._id && !d.resolved).map((d) => ({ markId: d.discussionId, element: d.element ?? null, quote: d.quote })));
  }, [discussions, handleTick, section?._id]); // eslint-disable-line react-hooks/exhaustive-deps
  const discussionsRef = useRef(discussions);
  discussionsRef.current = discussions;
  const pendingRef = useRef<string | null>(null);
  /** Je unbekannter Marke nur einmal nachladen — eine Marke ohne Faden (abgebrochen) lädt sonst bei jeder Eingabe. */
  const triedRef = useRef(new Set<string>());
  pendingRef.current = pendingComment?.discussionId ?? null;

  /** Kommentar-Knopf im Editor: Auswahl als Kommentar markieren, Faden rechts öffnen. */
  const requestComment = () => {
    const h = editorHandle.current;
    if (!h) return;
    const discussionId = `d${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
    const element = h.selectionElement();
    const quote = h.addComment(discussionId);
    if (!quote) {
      toast.error(t("guidelinesPage.comments.selectFirst"));
      return;
    }
    setPendingComment({ discussionId, quote, element });
    setTab("comments");
  };
  const cancelComment = () => {
    if (pendingComment) editorHandle.current?.removeComment(pendingComment.discussionId);
    setPendingComment(null);
  };
  const createComment = async (body: string) => {
    if (!pendingComment || !section) return;
    try {
      await api.createDiscussion(documentId, section._id, pendingComment.discussionId, pendingComment.quote, body, pendingComment.element);
      setSelectedDiscussion(pendingComment.discussionId);
      setPendingComment(null);
      // Die Marke steht im Editor — gleich speichern, damit der Faden verankert bleibt.
      await saveRef.current?.();
      await loadDiscussions();
    } catch (error) {
      toast.error(errorText(error));
    }
  };

  // Volltextsuche ab zwei Zeichen: Ergebnisliste statt Gliederung, Fundstellen im Abschnitt markiert.
  const searching = filter.trim().length >= 2;
  const results = useMemo(
    () => (searching ? searchSections(sections.map((s) => ({ _id: s._id, number: s.number, title: s.title, text: s.text, draftText: s.draft?.text ?? null })), filter) : null),
    [searching, sections, filter],
  );
  const searchTerms = useMemo(() => (searching ? parseQuery(filter) : []), [searching, filter]);

  if (!data || !section) return null;


  return (
    <div className="flex h-[calc(100vh-6.5rem)] min-h-[32rem] flex-col gap-3">
      <div className="flex flex-wrap items-baseline gap-x-6 gap-y-1">
        <div className="flex items-baseline gap-2">
          <button
            type="button"
            className="inline-flex items-center gap-1 self-center rounded-md px-1.5 py-1 text-sm text-muted-foreground hover:bg-muted hover:text-foreground"
            onClick={() => navigate(GUIDELINES_PATH)}
            aria-label={t("guidelinesPage.backToOverview")}
            title={t("guidelinesPage.backToOverview")}
          >
            <ArrowLeft className="h-4 w-4" />
            <span className="hidden sm:inline">{t("guidelinesPage.backToOverview")}</span>
          </button>
          <h2 className="text-lg font-semibold">{displayTitle(data.document.title)}</h2>
        </div>
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
          <span>{t("guidelinesPage.stats.sections", { count: stats.sections })}</span>
          <span>{t("guidelinesPage.stats.requirements", { muss: stats.muss, soll: stats.soll })}</span>
          <span>{t("guidelinesPage.stats.links", { confirmed: stats.confirmed, proposed: stats.proposed })}</span>
          <span>{t("guidelinesPage.stats.unlinked", { count: stats.unlinked })}</span>
          {stats.edited || stats.added || stats.removed ? (
            <span className="font-medium text-foreground">
              {t("guidelinesPage.stats.drafts", { edited: stats.edited, added: stats.added, removed: stats.removed })}
            </span>
          ) : null}
        </div>
        <div className="ml-auto flex items-center gap-2">
          {!data.document.supersededBy && (data.document.permissions.editText || data.document.permissions.approve) ? (
            <Button size="sm" variant={data.document.openEdition ? "default" : "outline"} onClick={() => setEditionOpen(true)}>
              <BookCheck size={14} className="mr-1" />
              {data.document.openEdition ? t("guidelinesPage.edition.openButton", { label: data.document.openEdition.label }) : t("guidelinesPage.edition.button")}
            </Button>
          ) : null}
          <ExportMenu customer={customer} documentId={documentId} />
          {data.document.workingGroup ? <Badge variant="outline">{data.document.workingGroup.name}</Badge> : null}
          <Badge variant={data.document.writable ? "default" : "outline"} title={t(`guidelinesPage.accessHint.${accessLevel(data.document.permissions)}`)}>
            {t(`guidelinesPage.access.${accessLevel(data.document.permissions)}`)}
          </Badge>
        </div>
      </div>

      {data.document.openEdition ? (
        <div className="rounded-md border border-warning bg-warning/10 px-3 py-2 text-sm">
          {t("guidelinesPage.edition.lockedBanner", {
            label: data.document.openEdition.label,
            date: new Date(data.document.openEdition.submittedAt).toLocaleDateString("de-DE"),
          })}
        </div>
      ) : null}
      {data.document.supersededBy ? (
        <div className="rounded-md border bg-muted px-3 py-2 text-sm">
          {t("guidelinesPage.edition.supersededBanner", { label: data.document.supersededBy.edition })}{" "}
          <button type="button" className="underline" onClick={() => navigate(`${GUIDELINES_PATH}/${data.document.supersededBy!._id}`)}>
            {t("guidelinesPage.edition.goToCurrent")}
          </button>
        </div>
      ) : null}
      {data.document.previousEdition ? (
        <div className="rounded-md border bg-muted/50 px-3 py-2 text-sm text-muted-foreground">
          {t("guidelinesPage.edition.fromWorkshop")}{" "}
          <button type="button" className="underline" onClick={() => navigate(`${GUIDELINES_PATH}/${data.document.previousEdition}`)}>
            {t("guidelinesPage.edition.goToPrevious")}
          </button>
        </div>
      ) : null}
      <EditionDialog
        customer={customer}
        documentId={documentId}
        open={editionOpen}
        onOpenChange={setEditionOpen}
        onChanged={(newDocument) => (newDocument ? navigate(`${GUIDELINES_PATH}/${newDocument}`) : reload())}
      />

      <ResizablePanelGroup direction="horizontal" className="min-h-0 flex-1 rounded-lg border bg-card">
        <ResizablePanel defaultSize={22} minSize={14}>
          <div className="flex h-full flex-col">
            <div className="border-b p-2">
              <Input
                type="search"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Escape") setFilter("");
                  if (e.key === "Enter" && results?.[0]) selectSection(sections.find((s) => s._id === results[0].sectionId)?.number ?? "");
                }}
                placeholder={t("guidelinesPage.search.placeholder")}
                aria-label={t("guidelinesPage.search.placeholder")}
              />
            </div>
            <nav className="min-h-0 flex-1 overflow-y-auto py-1" aria-label={t("guidelinesPage.outline")}>
              {results ? (
                <SearchResults results={results} selectedId={section._id} onSelect={(id) => selectSection(sections.find((s) => s._id === id)?.number ?? "")} />
              ) : (
              <SectionTree
                customer={customer}
                documentId={documentId}
                sections={sections}
                filtered={false}
                selectedId={section._id}
                writable={data.document.permissions.editStructure && !data.document.openEdition}
                openProposals={(id) => (linksBySection.get(id) ?? []).filter((l) => l.status === "proposed").length}
                onSelect={selectSection}
                onChanged={async (number) => {
                  await reload();
                  if (number) selectSection(number);
                }}
              />
              )}
            </nav>
          </div>
        </ResizablePanel>
        <ResizableHandle withHandle />
        <ResizablePanel defaultSize={48} minSize={30}>
          <SectionEditor
            key={section._id}
            customer={customer}
            documentId={documentId}
            section={section}
            links={linksBySection.get(section._id) ?? []}
            canEdit={data.document.permissions.editText}
            canComment={data.document.permissions.comment}
            locked={!!data.document.openEdition}
            hasPdf={data.document.hasPdf}
            currentUser={currentUser}
            onHandle={(h) => {
              editorHandle.current = h;
              editorSnapshot.current = h ? JSON.stringify(h.getValue()) : null;
              setHandleTick((n) => n + 1);
              setLiveSuggestions(h ? h.getSuggestions() : null);
            }}
            onEditorChange={onEditorChange}
            onCommentRequest={requestComment}
            onCommentClick={(id) => {
              setSelectedDiscussion(id);
              setTab("comments");
            }}
            saveRef={saveRef}
            showRequirements={showRequirements}
            onToggleRequirements={() => setShowRequirements((v) => !v)}
            selectedLink={selectedLink}
            onSelectLink={(id) => {
              setSelectedLink(id);
              setTab("links");
            }}
            onSaved={updateSection}
            onReload={reload}
            searchTerms={searchTerms}
            onDiscussionsChanged={loadDiscussions}
            currentUserName={currentUserName}
            view={view}
            onView={setView}
          />
        </ResizablePanel>
        <ResizableHandle withHandle />
        <ResizablePanel defaultSize={30} minSize={20}>
          <Tabs value={tab} onValueChange={setTab} className="flex h-full flex-col">
            <div className="border-b p-2">
              {/* Sechs Reiter in einer schmalen Spalte: umbrechen statt waagerecht scrollen. */}
              <TabsList className="flex-wrap gap-x-4 gap-y-1">
                <TabsTrigger value="links">{t("guidelinesPage.tabs.links")}</TabsTrigger>
                <TabsTrigger value="requirements">{t("guidelinesPage.tabs.requirements")}</TabsTrigger>
                <TabsTrigger value="comments">
                  {t("guidelinesPage.tabs.comments")}
                  {sectionDiscussions.filter((d) => !d.resolved).length ? ` (${sectionDiscussions.filter((d) => !d.resolved).length})` : ""}
                </TabsTrigger>
                <TabsTrigger value="suggestions">
                  {t("guidelinesPage.tabs.suggestions")}
                  {sectionSuggestions.length ? ` (${sectionSuggestions.length})` : ""}
                </TabsTrigger>
                <TabsTrigger value="review">{t("guidelinesPage.tabs.review")}</TabsTrigger>
                {data.document.permissions.comment ? <TabsTrigger value="terms">{t("guidelinesPage.tabs.terms")}</TabsTrigger> : null}
              </TabsList>
            </div>
            <TabsContent value="links" className="mt-0 min-h-0 flex-1 overflow-y-auto p-3">
              <LinkList
                customer={customer}
                documentId={documentId}
                links={linksBySection.get(section._id) ?? []}
                writable={data.document.permissions.reviewLinks}
                selected={selectedLink}
                onStatus={updateLink}
              />
            </TabsContent>
            <TabsContent value="requirements" className="mt-0 min-h-0 flex-1 overflow-y-auto p-3">
              {section.hasBlocks ? (
                <RequirementsPanel
                  customer={customer}
                  documentId={documentId}
                  sectionId={section._id}
                  canEdit={data.document.permissions.editText}
                  canReview={data.document.permissions.reviewLinks}
                  locked={!!data.document.openEdition}
                  refreshKey={String(section.draft?.at ?? "")}
                />
              ) : (
                <RequirementList section={section} />
              )}
            </TabsContent>
            <TabsContent value="comments" className="mt-0 min-h-0 flex-1 overflow-y-auto p-3">
              {data.document.permissions.comment ? (
                <CommentsPanel
                  discussions={sectionDiscussions}
                  pending={pendingComment}
                  selected={selectedDiscussion}
                  canResolveAll={data.document.permissions.editText}
                  currentUser={currentUser}
                  onCreate={createComment}
                  onCancelPending={cancelComment}
                  onReply={(id, body) => api.replyDiscussion(documentId, id, body).then(loadDiscussions).catch((error) => toast.error(errorText(error)))}
                  onResolve={(id, resolved) => api.resolveDiscussion(documentId, id, resolved).then(loadDiscussions).catch((error) => toast.error(errorText(error)))}
                  onSelect={setSelectedDiscussion}
                />
              ) : (
                <p className="text-sm text-muted-foreground">{t("guidelinesPage.comments.membersOnly")}</p>
              )}
            </TabsContent>
            <TabsContent value="suggestions" className="mt-0 min-h-0 flex-1 overflow-y-auto p-3">
              <SuggestionsPanel
                suggestions={sectionSuggestions}
                editing={liveSuggestions !== null}
                canDecide={data.document.permissions.editText}
                onAccept={(id) => editorHandle.current?.accept(id)}
                onReject={(id) => editorHandle.current?.reject(id)}
              />
            </TabsContent>
            <TabsContent value="review" className="mt-0 min-h-0 flex-1 overflow-y-auto p-3">
              <ReviewPanel customer={customer} documentId={documentId} />
            </TabsContent>
            {data.document.permissions.comment ? (
              <TabsContent value="terms" className="mt-0 min-h-0 flex-1 overflow-y-auto p-3">
                <TermsPanel customer={customer} documentId={documentId} canInsert={liveSuggestions !== null} onInsert={(term) => editorHandle.current?.insertTerm(term)} />
              </TabsContent>
            ) : null}
          </Tabs>
        </ResizablePanel>
      </ResizablePanelGroup>
    </div>
  );
}

function SectionEditor(props: {
  customer: string;
  documentId: string;
  section: GuidelineSection;
  links: GuidelineLink[];
  /** Direkt bearbeiten (Autor, Leitung) und Kommentieren/Vorschlagen (jedes Mitglied). */
  canEdit: boolean;
  canComment: boolean;
  locked: boolean;
  showRequirements: boolean;
  onToggleRequirements: () => void;
  selectedLink: string | null;
  onSelectLink: (id: string) => void;
  onSaved: (section: GuidelineSection) => void;
  /** Arbeitsbereich neu laden (nach dem gemeinsamen Bearbeiten). */
  onReload: () => void;
  /** Kommentarfäden neu laden (ein anderer hat kommentiert, geantwortet oder erledigt). */
  onDiscussionsChanged: () => void;
  /** Begriffe der Volltextsuche — im Abschnitt markiert. */
  searchTerms: string[];
  currentUserName: string;
  view: "pdf" | "text";
  onView: (view: "pdf" | "text") => void;
  /** Nur die Originalausgabe hat ein PDF. */
  hasPdf: boolean;
  currentUser: string;
  /** Der Editor ist bereit (oder geschlossen: null) — die Werkstatt nimmt Kommentare und Vorschläge darüber an. */
  onHandle: (handle: PlateEditorHandle | null) => void;
  onEditorChange: () => void;
  onCommentRequest: () => void;
  onCommentClick: (discussionId: string) => void;
  /** Speichern auslösen, ohne den Editor zu schließen (nach einem neuen Kommentar). */
  saveRef: React.MutableRefObject<(() => Promise<void>) | null>;
}) {
  const { t } = useTranslation();
  const api = useGuidelinesAPI(props.customer);
  // Abgleich mit dem Glossar: weicht die Definition dieses Begriffsabschnitts (auch im Entwurf) ab?
  const [glossaryHit, setGlossaryHit] = useState<{ term: string; definition: string } | null>(null);
  useEffect(() => {
    let live = true;
    api
      .getGlossary(props.documentId)
      .then((v) => {
        const entry = v.entries.find((e) => e.occurrences.some((o) => o.sectionId === props.section._id && o.state === "different"));
        if (live) setGlossaryHit(entry ? { term: entry.term, definition: entry.reference ?? "" } : null);
      })
      .catch(() => live && setGlossaryHit(null));
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.documentId, props.section._id, props.section.draft?.at]);
  const { section } = props;
  const current = section.draft?.text ?? section.text;
  // null = lesen, "edit" = direkt bearbeiten, "suggest" = Änderungen vorschlagen
  const [mode, setMode] = useState<null | "edit" | "suggest">(null);
  const editing = mode !== null;
  const handle = useRef<PlateEditorHandle | null>(null);
  const openedWith = useRef<string | null>(null);
  const onEditorReady = useCallback(
    (h: PlateEditorHandle) => {
      handle.current = h;
      openedWith.current = JSON.stringify(h.getValue());
      props.onHandle(h);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  useEffect(() => () => props.onHandle(null), []); // eslint-disable-line react-hooks/exhaustive-deps
  // Editor geschlossen: kein Zugriff mehr auf ihn (Vorschläge annehmen liefe sonst ins Leere).
  useEffect(() => {
    if (!editing) props.onHandle(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing]);
  const editorLabels = useMemo(
    () =>
      Object.fromEntries(
        ["editor", "bold", "italic", "heading2", "heading3", "quote", "bulletList", "numberedList", "table", "tableRow", "tableColumn", "deleteRow", "deleteColumn", "comment", "image", "deleteImage", "suggestingBadge", "live", "connecting", "alsoHere", "footnote", "footnotes", "footnotePlaceholder", "formula", "formulaInsertTitle", "formulaEditTitle", "formulaIntro", "formulaTex", "formulaDisplay", "formulaLabel", "formulaPreview", "formulaEmpty", "formulaSave", "formulaRemove", "formulaCancel", "formulaCheat", "formulaCheatHint", "formulaCheatBasics", "formulaCheatRelations", "formulaCheatGreek", "formulaCheatOperators", "formulaCheatUnits", "formulaCheatStructures", "formulaCheatExamples"].map((k) => [k, t(`guidelinesPage.toolbar.${k}`)]),
      ) as unknown as PlateMarkdownEditorLabels,
    [t],
  );
  const [saving, setSaving] = useState(false);
  const [stsOpen, setStsOpen] = useState(false);
  const [versionsOpen, setVersionsOpen] = useState(false);
  const uploadImage = useCallback(
    // Wie Logo und Grundriss: über die allgemeine Dateiablage, im Text steht der Pfad `/documents/…`.
    async (file: File) => {
      const form = new FormData();
      form.append("file", file);
      try {
        const result = await userAPI.uploadFile(form);
        if (!result?.path) throw new Error(result?.error ?? result?.message ?? "—");
        return { url: result.path as string, name: file.name };
      } catch (error) {
        toast.error(t("guidelinesPage.imageUploadFailed", { reason: errorText(error) }));
        throw error;
      }
    },
    [t],
  );
  // Blockmodell: Inhalt je Abschnitt nachladen; der Entwurf geht vor.
  const [content, setContent] = useState<{ key: string; value: SectionContent } | null>(null);
  const contentKey = `${section._id}:${String(section.draft?.at ?? "")}`;
  useEffect(() => {
    if (!section.hasBlocks) return;
    let live = true;
    api
      .getSectionContent(props.documentId, section._id)
      .then((c) => live && setContent({ key: contentKey, value: c.draft ?? { blocks: c.blocks ?? [], footnotes: c.footnotes } }))
      .catch((error) => toast.error(errorText(error)));
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contentKey, section.hasBlocks]);
  const blocks = section.hasBlocks ? (content?.key === contentKey ? content.value : undefined) : null;
  const removed = !!section.draft?.removed;
  // „Abschnitt entwerfen“: das Modell braucht einige Sekunden; danach stehen die Vorschläge im Arbeitsstand.
  const [drafting, setDrafting] = useState(false);
  const proposeDraft = async () => {
    setDrafting(true);
    try {
      const r = await api.proposeSectionDraft(props.documentId, section._id);
      const sources = r.sources.map((s) => s.citation).join(", ");
      toast.success(t("guidelinesPage.sectionDraft.done", { count: r.suggestions, sources: sources || t("guidelinesPage.sectionDraft.noSources") }));
      if (r.demoted) toast.info(t("guidelinesPage.sectionDraft.demoted", { count: r.demoted }));
      props.onReload();
    } catch (error) {
      toast.error(errorText(error));
    } finally {
      setDrafting(false);
    }
  };
  // Das PDF zeigt die geltende Fassung: nur für bestehende Abschnitte mit Seitenangabe, nicht beim Bearbeiten.
  const pdfPossible = props.hasPdf && !!section.pageFrom && !section.draft?.isNew;
  const showPdf = props.view === "pdf" && pdfPossible && !editing;

  /** Speichern: Markdown ohne offene Vorschläge, Plate-Inhalt mit allen Marken. */
  const persist = async (close: boolean, discard = false) => {
    setSaving(true);
    try {
      const h = handle.current;
      const result = discard
        ? await api.saveDraft(props.documentId, section._id, "")
        : section.hasBlocks
          ? await api.saveDraft(props.documentId, section._id, "", undefined, h?.getValue(), h?.getSuggestions().length ?? 0, h?.getBlocks() ?? blocks ?? null)
          : await api.saveDraft(props.documentId, section._id, h ? h.getMarkdown() : current, undefined, h?.getValue(), h?.getSuggestions().length ?? 0);
      props.onSaved({ ...section, title: result.title, draft: result.draft, requirements: result.requirements });
      if (close) {
        setMode(null);
        toast.success(result.draft ? t("guidelinesPage.draftSaved") : t("guidelinesPage.draftDiscarded"));
      }
    } catch (error) {
      toast.error(errorText(error));
    } finally {
      setSaving(false);
    }
  };
  // Gemeinsames Bearbeiten für Abschnitte mit Blockmodell: ein Live-Dokument je Abschnitt.
  const collab = useMemo(
    () =>
      section.hasBlocks && editing && !props.locked
        ? {
            document: `guideline/${props.documentId}/${section._id}`,
            url: `${configuration.serverUrl.replace(/^http/, "ws")}/collab`,
            // Bei jedem (Neu-)Verbinden ein gültiges Token — ein abgelaufenes erst erneuern.
            token: async () => {
              const current = jwtAuthService.getAccessToken();
              if (current && !jwtAuthService.isTokenExpired(current)) return current;
              return (await jwtAuthService.refreshAccessToken()) ?? "";
            },
            user: { name: props.currentUserName || props.currentUser, color: colorFor(props.currentUser) },
            // Der Editor hält die Verbindung; die Nachricht geht über die Ref an den aktuellen Handler.
            onMessage: (message: { type: string }) => onMessageRef.current(message),
          }
        : null,
    [section.hasBlocks, section._id, editing, props.locked, props.documentId, props.currentUser, props.currentUserName],
  );
  const onMessageRef = useRef<(message: { type: string }) => void>(() => undefined);
  onMessageRef.current = (message) => {
    if (message.type === "discussions") props.onDiscussionsChanged();
    if (message.type === "restored") props.onReload();
  };
  // Fassungen gibt es nur im Blockmodell.
  const versionsButton = section.hasBlocks ? (
    <Button size="icon" variant="ghost" className="h-8 w-8" title={t("guidelinesPage.versions.button")} aria-label={t("guidelinesPage.versions.button")} onClick={() => setVersionsOpen(true)}>
      <History size={15} />
    </Button>
  ) : null;
  /** Editor schließen; der Server speichert beim Verlassen sofort, danach den Stand neu laden. */
  const reloadTimer = useRef<number | null>(null);
  useEffect(() => () => {
    if (reloadTimer.current) window.clearTimeout(reloadTimer.current);
  }, []);
  const finishLive = () => {
    setMode(null);
    reloadTimer.current = window.setTimeout(() => props.onReload(), 1500);
  };
  props.saveRef.current = editing ? (collab ? async () => undefined : () => persist(false)) : null;

  const activeLinks = props.links.filter((l) => l.status !== "rejected");
  // Stellen im Abschnittstext gelten für die angezeigte Fassung — mit Textentwurf nur die Belege selbst.
  const hasTextDraft = section.draft?.text != null;
  const pdfMarks: PdfMark[] = [
    ...(props.showRequirements && !hasTextDraft ? section.requirements.map((r) => ({ text: section.text.slice(r.start, r.end), kind: r.kind })) : []),
    ...activeLinks
      .filter((l) => l.evidence)
      .map((l) => ({
        text: l.span && !hasTextDraft ? section.text.slice(l.span.start, l.span.end) : l.evidence!,
        kind: "link" as const,
        linkId: l._id,
      })),
  ];
  // In der Leseansicht ist alles an der angezeigten Fassung ausgerichtet (Entwurf oder geltend).
  const viewMarks: TextMark[] = useMemo(
    () => [
      ...(props.showRequirements ? section.requirements.map((r) => ({ text: current.slice(r.start, r.end), kind: r.kind })) : []),
      ...activeLinks.map((l) => ({ text: l.span ? current.slice(l.span.start, l.span.end) : l.evidence ?? "", kind: "link" as const, linkId: l._id })).filter((m) => m.text),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.showRequirements, section, props.links, current],
  );
  const viewRoot = useRef<HTMLElement | null>(null);
  const viewLinks = useRef<Array<{ range: Range; linkId: string }>>([]);
  const searchRef = useRef(props.searchTerms);
  searchRef.current = props.searchTerms;
  const onRendered = useCallback((root: HTMLElement) => {
    viewRoot.current = root;
    viewLinks.current = paintMarks([root], viewMarksRef.current, selectedRef.current);
    // Erste Fundstelle der Suche in den Blick holen.
    paintSearchTerms([root], searchRef.current)?.startContainer.parentElement?.scrollIntoView({ block: "center" });
  }, []);
  const viewMarksRef = useRef(viewMarks);
  viewMarksRef.current = viewMarks;
  const selectedRef = useRef(props.selectedLink);
  selectedRef.current = props.selectedLink;
  useEffect(() => {
    if (viewRoot.current && !editing && !showPdf) viewLinks.current = paintMarks([viewRoot.current], viewMarks, props.selectedLink);
  }, [viewMarks, props.selectedLink, editing, showPdf]);
  const searchKey = props.searchTerms.join("\u0000");
  useEffect(() => {
    if (viewRoot.current && !editing && !showPdf) paintSearchTerms([viewRoot.current], props.searchTerms);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchKey, editing, showPdf]);
  useEffect(() => () => clearMarks(), []);

  return (
    <div className="flex h-full flex-col">
      <div className="flex flex-wrap items-center gap-2 border-b p-2">
        {/* Mindestbreite: lieber umbrechen als den Titel auf ein Zeichen stauchen. */}
        <div className="min-w-[12rem] flex-1">
          <div className="truncate font-semibold" title={[displayNumber(section), section.title].filter(Boolean).join(" ")}>
            {section.number !== section.title ? `${displayNumber(section)} ${section.title}` : section.title}
          </div>
          <div className="text-xs text-muted-foreground">
            {section.pageFrom ? t("guidelinesPage.pages", { from: section.pageFrom, to: section.pageTo ?? section.pageFrom }) : null}
            {section.draft ? ` · ${t("guidelinesPage.draftSince", { date: new Date(section.draft.at).toLocaleString("de-DE") })}` : ""}
            {section.draft?.openSuggestions ? ` · ${t("guidelinesPage.suggestions.open", { count: section.draft.openSuggestions })}` : ""}
          </div>
          {glossaryHit ? (
            <div className="mt-1 flex items-start gap-1 text-xs text-amber-700 dark:text-amber-300" title={glossaryHit.definition} data-testid="glossary-hint">
              <AlertTriangle size={12} className="mt-0.5 shrink-0" aria-hidden />
              {t("guidelinesPage.glossary.sectionDiffers", { term: glossaryHit.term })}
            </div>
          ) : null}
        </div>
        {editing ? (
          <>
            {versionsButton}
            {props.canEdit ? (
              <div className="flex rounded-md border p-0.5 text-xs" role="group" aria-label={t("guidelinesPage.modeLabel")}>
                {(["edit", "suggest"] as const).map((m) => (
                  <button key={m} type="button" onClick={() => setMode(m)} aria-pressed={mode === m} className={cn("rounded px-2 py-1", mode === m ? "bg-accent font-medium" : "text-muted-foreground")}>
                    {t(`guidelinesPage.modes.${m}`)}
                  </button>
                ))}
              </div>
            ) : null}
            {collab ? (
              // Gemeinsames Bearbeiten: der Server speichert laufend; „Fertig“ schließt nur den Editor.
              <Button size="sm" onClick={finishLive}>
                <Check size={14} className="mr-1" />
                {t("guidelinesPage.collab.done")}
              </Button>
            ) : (
              <>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    const h = handle.current;
                    if (h && openedWith.current !== null && JSON.stringify(h.getValue()) !== openedWith.current && !window.confirm(t("guidelinesPage.unsavedConfirm"))) return;
                    setMode(null);
                  }}
                  disabled={saving}
                >
                  <X size={14} className="mr-1" />
                  {t("guidelinesPage.cancel")}
                </Button>
                <Button size="sm" onClick={() => persist(true)} disabled={saving}>
                  <Save size={14} className="mr-1" />
                  {t("guidelinesPage.saveDraft")}
                </Button>
              </>
            )}
          </>
        ) : (
          <>
            {pdfPossible ? (
              <div className="flex rounded-md border p-0.5 text-xs" role="group" aria-label={t("guidelinesPage.view")}>
                {(["pdf", "text"] as const).map((v) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => props.onView(v)}
                    className={cn("rounded px-2 py-1", props.view === v ? "bg-accent font-medium" : "text-muted-foreground")}
                    aria-pressed={props.view === v}
                  >
                    {t(`guidelinesPage.views.${v}`)}
                  </button>
                ))}
              </div>
            ) : null}
            {versionsButton}
            <Button size="icon" variant="ghost" className="h-8 w-8" title={t("guidelinesPage.sts.button")} aria-label={t("guidelinesPage.sts.button")} onClick={() => setStsOpen(true)}>
              <Code2 size={15} />
            </Button>
            <Button size="sm" variant="ghost" onClick={props.onToggleRequirements}>
              {props.showRequirements ? t("guidelinesPage.hideRequirements") : t("guidelinesPage.showRequirements")}
            </Button>
            {section.draft?.text != null && !section.draft.isNew && props.canEdit && !props.locked ? (
              <Button size="sm" variant="ghost" onClick={() => persist(true, true)} disabled={saving}>
                <RotateCcw size={14} className="mr-1" />
                {t("guidelinesPage.discardDraft")}
              </Button>
            ) : null}
            {props.canEdit && section.hasBlocks && !props.locked && !removed ? (
              <Button size="sm" variant="ghost" onClick={proposeDraft} disabled={drafting} title={t("guidelinesPage.sectionDraft.hint")} data-testid="section-draft">
                <Sparkles size={14} className="mr-1" />
                {drafting ? t("guidelinesPage.sectionDraft.busy") : t("guidelinesPage.sectionDraft.button")}
              </Button>
            ) : null}
            <Button size="sm" variant="ghost" onClick={() => setMode("suggest")} disabled={!props.canComment || props.locked || removed}>
              <MessageSquarePlus size={14} className="mr-1" />
              {t("guidelinesPage.suggestAndComment")}
            </Button>
            <Button size="sm" variant="outline" onClick={() => setMode("edit")} disabled={!props.canEdit || props.locked || removed}>
              <Pencil size={14} className="mr-1" />
              {t("guidelinesPage.edit")}
            </Button>
          </>
        )}
      </div>
      <VersionsDialog
        customer={props.customer}
        documentId={props.documentId}
        sectionId={section._id}
        heading={`${displayNumber(section)} ${section.title}`}
        canRestore={props.canEdit && !props.locked}
        open={versionsOpen}
        onOpenChange={setVersionsOpen}
        onRestored={() => props.onReload()}
      />
      <StsDialog customer={props.customer} documentId={props.documentId} sectionId={section._id} heading={`${displayNumber(section)} ${section.title}`} open={stsOpen} onOpenChange={setStsOpen} />
      {removed ? (
        <div className="border-b bg-destructive/10 p-2 text-sm">
          {t("guidelinesPage.removedBanner", { links: activeLinks.length, requirements: section.requirements.length })}
        </div>
      ) : null}
      {showPdf ? (
        <GuidelinePdfView
          customer={props.customer}
          documentId={props.documentId}
          pageFrom={section.pageFrom!}
          pageTo={section.pageTo ?? section.pageFrom!}
          heading={`${displayNumber(section)} ${section.title}`}
          marks={pdfMarks}
          searchTerms={props.searchTerms}
          selectedLink={props.selectedLink}
          onSelectLink={props.onSelectLink}
        />
      ) : (
      <div className="min-h-0 flex-1 overflow-y-auto p-4">
        <Suspense fallback={<p className="text-sm text-muted-foreground">{t("guidelinesPage.editorLoading")}</p>}>
          {editing ? (
            blocks === undefined ? (
              <p className="text-sm text-muted-foreground">{t("guidelinesPage.editorLoading")}</p>
            ) : (
            <PlateMarkdownEditor
              markdown={current}
              value={section.draft?.value ?? null}
              blocks={blocks}
              labels={editorLabels}
              currentUserId={props.currentUser}
              canEdit={props.canEdit}
              suggesting={mode === "suggest"}
              onReady={onEditorReady}
              onChange={props.onEditorChange}
              onCommentRequest={props.onCommentRequest}
              onCommentClick={props.onCommentClick}
              onImageUpload={props.canEdit ? uploadImage : undefined}
              collab={collab}
            />
            )
          ) : (
            <>
              <div
                className={cn(removed && "text-muted-foreground line-through")}
                onClick={(e) => {
                  const linkId = linkAtPoint(e.clientX, e.clientY, viewLinks.current);
                  if (linkId) props.onSelectLink(linkId);
                }}
              >
                {blocks === undefined ? (
                  <p className="text-sm text-muted-foreground">{t("guidelinesPage.editorLoading")}</p>
                ) : (
                  <PlateMarkdownView markdown={current} value={section.draft?.value ?? null} blocks={blocks} onRendered={onRendered} onCommentClick={props.onCommentClick} />
                )}
              </div>
              <div className="mt-6 flex flex-wrap gap-3 border-t pt-3 text-xs text-muted-foreground">
                <span><span className={cn("px-1", REQUIREMENT_CLASS.muss)}>{t("guidelinesPage.kind.muss")}</span></span>
                <span><span className={cn("px-1", REQUIREMENT_CLASS.soll)}>{t("guidelinesPage.kind.soll")}</span></span>
                <span><span className={cn("px-1", REQUIREMENT_CLASS.kann)}>{t("guidelinesPage.kind.kann")}</span></span>
                <span className="underline decoration-primary decoration-2 underline-offset-4">{t("guidelinesPage.legendLink")}</span>
                <span><span className="rounded-sm bg-amber-200/60 px-1 dark:bg-amber-700/40">{t("guidelinesPage.legendComment")}</span></span>
                <span><span className="bg-emerald-100 px-1 dark:bg-emerald-900/40">{t("guidelinesPage.legendInsert")}</span></span>
                <span><span className="bg-rose-100 px-1 line-through dark:bg-rose-900/40">{t("guidelinesPage.legendRemove")}</span></span>
              </div>
              {section.unresolvedReferences.length ? (
                <div className="mt-4 text-xs">
                  <div className="font-medium">{t("guidelinesPage.unresolved")}</div>
                  <ul className="mt-1 list-disc pl-5 text-muted-foreground">
                    {section.unresolvedReferences.map((r) => <li key={r}>{r}</li>)}
                  </ul>
                </div>
              ) : null}
            </>
          )}
        </Suspense>
      </div>
      )}
    </div>
  );
}

function LinkList(props: {
  customer: string;
  documentId: string;
  links: GuidelineLink[];
  writable: boolean;
  selected: string | null;
  onStatus: (link: GuidelineLink, status: LinkStatus) => void;
}) {
  const { t } = useTranslation();
  const [reading, setReading] = useState<GuidelineLink["target"] | null>(null);
  if (!props.links.length) return <p className="text-sm text-muted-foreground">{t("guidelinesPage.noLinks")}</p>;
  const groups = Array.from(new Set(props.links.map((l) => l.roleLabel)));

  return (
    <div className="flex flex-col gap-4">
      <ProvisionDialog customer={props.customer} documentId={props.documentId} target={reading ?? null} onOpenChange={(open) => !open && setReading(null)} />
      {groups.map((group) => (
        <div key={group} className="flex flex-col gap-2">
          <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{group}</div>
          {props.links
            .filter((l) => l.roleLabel === group)
            .sort((a, b) => (a.status === "proposed" ? -1 : 0) - (b.status === "proposed" ? -1 : 0))
            .map((link) => (
              <div
                key={link._id}
                data-link-id={link._id}
                className={cn(
                  "rounded-md border p-3 text-sm",
                  link._id === props.selected && "ring-2 ring-primary",
                  link.status === "rejected" && "opacity-60",
                )}
              >
                <div className="flex items-start gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="font-medium">{link.label}</div>
                    {link.regulationTitle ? <div className="text-xs text-muted-foreground">{link.regulationTitle}</div> : null}
                  </div>
                  <Badge variant={STATUS_VARIANT[link.status]}>{t(`guidelinesPage.status.${link.status}`)}</Badge>
                </div>
                {link.evidence ? (
                  <blockquote className="mt-2 border-l-2 pl-2 text-xs italic text-muted-foreground">… {link.evidence} …</blockquote>
                ) : null}
                {link.target && (link.hasText || link.hasProvisions || link.officialUrl) ? (
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    {link.hasText || link.hasProvisions ? (
                      <Button size="sm" variant="secondary" className="h-7 px-2" onClick={() => setReading(link.target ?? null)}>
                        <BookOpen size={14} className="mr-1" />
                        {t(link.hasText ? "guidelinesPage.provision.read" : "guidelinesPage.provision.contents")}
                      </Button>
                    ) : null}
                    {link.officialUrl ? (
                      <Button asChild size="sm" variant="ghost" className="h-7 px-2">
                        <a href={link.officialUrl} target="_blank" rel="noopener noreferrer" title={link.officialUrl}>
                          <ExternalLink size={14} className="mr-1" />
                          {link.officialLabel ?? t("guidelinesPage.provision.official")}
                        </a>
                      </Button>
                    ) : null}
                  </div>
                ) : null}
                <div className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
                  <span>
                    {t(`guidelinesPage.origin.${link.extractor?.startsWith("citation") ? "citation" : "semantic"}`)}
                    {link.confidence != null && link.confidence < 1 ? ` · ${Math.round(link.confidence * 100)} %` : ""}
                  </span>
                  <span className="flex-1" />
                  {props.writable && link.status !== "confirmed" ? (
                    <Button size="sm" variant="outline" className="h-7 px-2" onClick={() => props.onStatus(link, "confirmed")}>
                      <Check size={14} className="mr-1" />
                      {t("guidelinesPage.confirm")}
                    </Button>
                  ) : null}
                  {props.writable && link.status !== "rejected" ? (
                    <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => props.onStatus(link, "rejected")}>
                      <X size={14} className="mr-1" />
                      {t("guidelinesPage.reject")}
                    </Button>
                  ) : null}
                  {props.writable && link.status === "rejected" ? (
                    <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => props.onStatus(link, "proposed")}>
                      <RotateCcw size={14} className="mr-1" />
                      {t("guidelinesPage.restore")}
                    </Button>
                  ) : null}
                </div>
              </div>
            ))}
        </div>
      ))}
    </div>
  );
}

function RequirementList({ section }: { section: GuidelineSection }) {
  const { t } = useTranslation();
  const text = section.draft?.text ?? section.text;
  if (!section.requirements.length) return <p className="text-sm text-muted-foreground">{t("guidelinesPage.noRequirements")}</p>;

  return (
    <div className="flex flex-col gap-2">
      <p className="text-xs text-muted-foreground">{t("guidelinesPage.requirementsHint")}</p>
      {section.requirements.map((r, i) => (
        <div key={r.start} className="rounded-md border p-3 text-sm">
          <div className="mb-1 flex items-center gap-2 text-xs text-muted-foreground">
            <span className="tabular-nums">{section.number}-A{i + 1}</span>
            <span className={cn("rounded px-1.5", REQUIREMENT_CLASS[r.kind])}>{t(`guidelinesPage.kind.${r.kind}`)}</span>
          </div>
          {text.slice(r.start, r.end)}
        </div>
      ))}
    </div>
  );
}

function ReviewPanel({ customer, documentId }: { customer: string; documentId: string }) {
  const { t } = useTranslation();
  const api = useGuidelinesAPI(customer);
  const [markdown, setMarkdown] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      setMarkdown((await api.getReview(documentId)).markdown);
    } catch (error) {
      toast.error(errorText(error));
    } finally {
      setLoading(false);
    }
  };

  if (markdown === null)
    return (
      <div className="flex flex-col items-start gap-3">
        <p className="text-sm text-muted-foreground">{t("guidelinesPage.reviewIntro")}</p>
        <Button size="sm" onClick={load} disabled={loading}>
          {loading ? t("guidelinesPage.reviewLoading") : t("guidelinesPage.reviewLoad")}
        </Button>
      </div>
    );

  return (
    <div className="guideline-review">
      <Markdown remarkPlugins={[remarkGfm]} components={{ a: ({ node, children, ...rest }) => <a {...rest} target="_blank" rel="noreferrer">{children}</a> }}>
        {markdown}
      </Markdown>
    </div>
  );
}
